import {
  AlertCircle,
  Check,
  ChevronDown,
  CircleDot,
  Copy,
  ExternalLink,
  GitPullRequest,
  Globe2,
  KeyRound,
  Layers3,
  PackageCheck,
  Plus,
  RefreshCw,
  Rocket,
  ScanSearch,
  Server,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  Trash2,
  Unplug
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  approveDeliveryCampaignTarget,
  cancelDeliveryCampaign,
  createDeliveryCampaign,
  createRuntimeObserver,
  listReleaseAppVersions,
  listReleaseApps,
  listReleaseArtifacts,
  listReleaseChannels,
  listRuntimeObservations,
  listRuntimeObservers,
  preflightDeliveryCampaign,
  retryDeliveryCampaignTarget,
  revokeRuntimeObserver,
  rotateRuntimeObserver,
  startDeliveryCampaign,
  type DeliveryCampaignRow,
  type DeliveryCandidateArtifactRow,
  type DeliveryCandidateRow,
  type IssuedRuntimeObserverConnectionRow,
  type ReleaseAppRow,
  type ReleaseAppVersionRow,
  type ReleaseArtifactRow,
  type ReleaseChannelRow,
  type RuntimeObservationRow,
  type RuntimeObserverConnectionRow
} from '../api';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatArtifactSummary, formatRelativeDate } from '../utils/formatters';
import {
  containerMatchesPlannerKind,
  selectCurrentContainerSources,
  summarizeRuntime,
  summarizeRuntimeImage
} from '../utils/release-control';
import {
  campaignTargetGateIssues,
  campaignWideGateIssues,
  deliveryCampaignTargetStatusLabel,
  deliveryCandidateStatusLabel,
  describeCampaignProgress,
  readyCampaignTargetKeys
} from '../utils/delivery-campaign-progress';
import { describeCampaignStartCondition } from '../utils/delivery-campaign-start-condition';
import { searchCampaignWorkJourneys } from '../utils/campaign-work-journeys';
import { buildWorkJourneyPath } from '../utils/routes';
import { useFeedback } from '../contexts/FeedbackContext';

type PlannerKind = 'api' | 'web' | 'ios' | 'android';
type DraftTarget = {
  key: string;
  kind: PlannerKind;
  sourceRef: string;
  environment: string;
  channel: string;
  dependencyKeys: string[];
  approvalRequired: boolean;
};
type ContainerSource = {
  ref: string;
  candidate: DeliveryCandidateRow;
  artifact: DeliveryCandidateArtifactRow;
};

const kindCopy: Record<PlannerKind, { label: string; description: string }> = {
  api: {
    label: 'API service',
    description: 'Container promoted by your GitOps workflow'
  },
  web: {
    label: 'Web app',
    description: 'Container promoted by your GitOps workflow'
  },
  ios: { label: 'iOS OTA', description: 'Bundle assigned to an iOS channel' },
  android: {
    label: 'Android OTA',
    description: 'Bundle assigned to an Android channel'
  }
};
function requestErrorMessage(caught: unknown, fallback: string) {
  if (typeof caught === 'object' && caught !== null && 'response' in caught) {
    const response = caught.response;
    if (
      typeof response === 'object' &&
      response !== null &&
      'data' in response
    ) {
      const data = response.data;
      if (
        typeof data === 'object' &&
        data !== null &&
        'message' in data &&
        typeof data.message === 'string'
      )
        return data.message;
    }
  }
  return caught instanceof Error && caught.message ? caught.message : fallback;
}

function exactContainerIdentity(artifact: DeliveryCandidateArtifactRow) {
  return artifact.digest || artifact.tag || artifact.artifactId || '';
}
function shortIdentity(value?: string) {
  if (!value) return 'Identity unavailable';
  const cleaned = value.replace(/^sha256:/, '');
  return cleaned.length > 22
    ? `${cleaned.slice(0, 12)}…${cleaned.slice(-7)}`
    : cleaned;
}
function environmentRank(environment: string) {
  const normalized = environment.toLowerCase();
  if (normalized === 'production' || normalized === 'prod') return 0;
  if (normalized === 'staging' || normalized === 'stage') return 1;
  if (normalized === 'development' || normalized === 'dev') return 2;
  return 3;
}
function targetIcon(kind: PlannerKind) {
  if (kind === 'api') return <Server size={17} />;
  if (kind === 'web') return <Globe2 size={17} />;
  return <Smartphone size={17} />;
}
function statusLabel(status: DeliveryCampaignRow['status']) {
  if (status === 'completed') return 'Delivery complete';
  if (status === 'planned') return 'Planned';
  return status.charAt(0).toUpperCase() + status.slice(1);
}
function storeReviewLabel(status?: ReleaseAppVersionRow['storeReviewStatus']) {
  if (status === 'in_review') return 'In review';
  if (status === 'not_submitted') return 'Not submitted';
  if (!status) return 'Not submitted';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function ShipmentsPage() {
  const { session } = useSession();
  const { confirm, toast } = useFeedback();
  const { workspace, activeOrganization, refreshWorkspace } = useWorkspace();
  const [view, setView] = useState<'state' | 'campaigns' | 'plan'>('state');
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  const [artifacts, setArtifacts] = useState<ReleaseArtifactRow[]>([]);
  const [channels, setChannels] = useState<ReleaseChannelRow[]>([]);
  const [releaseApps, setReleaseApps] = useState<ReleaseAppRow[]>([]);
  const [appVersions, setAppVersions] = useState<ReleaseAppVersionRow[]>([]);
  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [draftTargets, setDraftTargets] = useState<DraftTarget[]>([]);
  const [selectedWorkJourneyIds, setSelectedWorkJourneyIds] = useState<
    string[]
  >([]);
  const [workJourneySearch, setWorkJourneySearch] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [expandedCampaignId, setExpandedCampaignId] = useState('');
  const [runtimeObservers, setRuntimeObservers] = useState<
    RuntimeObserverConnectionRow[]
  >([]);
  const [runtimeObservations, setRuntimeObservations] = useState<
    RuntimeObservationRow[]
  >([]);
  const [showRuntimeSetup, setShowRuntimeSetup] = useState(false);
  const [issuedObserver, setIssuedObserver] =
    useState<IssuedRuntimeObserverConnectionRow | null>(null);
  const [observerDraft, setObserverDraft] = useState({
    projectId: '',
    name: 'Production Prometheus',
    environment: 'production',
    namespace: ''
  });
  const projectScopeKey = workspace.apps
    .map((project) => project.id)
    .sort()
    .join(',');

  useEffect(() => {
    if (!session || !activeOrganization) return;
    let cancelled = false;
    const projectIds = projectScopeKey.split(',').filter(Boolean);
    Promise.all([
      Promise.all(
        projectIds.map((projectId) =>
          listReleaseArtifacts(session.accessToken, {
            organizationId: activeOrganization.id,
            projectId
          }).catch(() => [])
        )
      ).then((rows) => rows.flat()),
      Promise.all(
        projectIds.map((projectId) =>
          listReleaseChannels(session.accessToken, {
            organizationId: activeOrganization.id,
            projectId
          }).catch(() => [])
        )
      ).then((rows) => rows.flat()),
      Promise.all(
        projectIds.map((projectId) =>
          listReleaseApps(session.accessToken, {
            organizationId: activeOrganization.id,
            projectId
          }).catch(() => [])
        )
      ).then((rows) => rows.flat())
    ])
      .then(async ([artifactRows, channelRows, releaseApps]) => {
        const versionRows = (
          await Promise.all(
            releaseApps.map((app) =>
              listReleaseAppVersions(session.accessToken, {
                organizationId: activeOrganization.id,
                projectId: app.projectId,
                releaseAppId: app.releaseAppId
              }).catch(() => [])
            )
          )
        ).flat();
        if (!cancelled) {
          setArtifacts(artifactRows);
          setChannels(channelRows);
          setReleaseApps(releaseApps);
          setAppVersions(versionRows);
        }
      })
      .catch((caught) => {
        if (!cancelled)
          setError(
            requestErrorMessage(caught, 'Could not load release inventory.')
          );
      });
    return () => {
      cancelled = true;
    };
  }, [activeOrganization, projectScopeKey, session]);

  useEffect(() => {
    if (!session || !activeOrganization) return;
    let cancelled = false;
    Promise.all([
      listRuntimeObservers(session.accessToken, activeOrganization.id),
      listRuntimeObservations(session.accessToken, {
        organizationId: activeOrganization.id
      })
    ])
      .then(([connections, observations]) => {
        if (!cancelled) {
          setRuntimeObservers(connections);
          setRuntimeObservations(observations);
        }
      })
      .catch((caught) => {
        if (!cancelled)
          setError(
            requestErrorMessage(
              caught,
              'Could not load Kubernetes runtime evidence.'
            )
          );
      });
    return () => {
      cancelled = true;
    };
  }, [activeOrganization, session]);

  const containerSources = useMemo<ContainerSource[]>(
    () =>
      [...workspace.deliveryCandidates]
        .filter(
          (candidate) =>
            candidate.status !== 'failed' && candidate.status !== 'closed'
        )
        .sort(
          (left, right) =>
            new Date(right.updated || right.created || 0).getTime() -
            new Date(left.updated || left.created || 0).getTime()
        )
        .flatMap((candidate) =>
          candidate.artifacts
            .filter(
              (artifact) =>
                artifact.kind === 'container' &&
                exactContainerIdentity(artifact)
            )
            .map((artifact) => ({
              ref: `candidate:${candidate.deliveryCandidateId}:${artifact.key}`,
              candidate,
              artifact
            }))
        ),
    [workspace.deliveryCandidates]
  );
  const currentContainerSources = useMemo<ContainerSource[]>(
    () =>
      selectCurrentContainerSources(workspace.deliveryCandidates).map(
        ({ candidate, artifact }) => ({
          ref: `candidate:${candidate.deliveryCandidateId}:${artifact.key}`,
          candidate,
          artifact
        })
      ),
    [workspace.deliveryCandidates]
  );
  const otaArtifacts = useMemo(
    () =>
      artifacts
        .filter((artifact) => artifact.status !== 'archived')
        .sort(
          (a, b) =>
            new Date(b.buildGeneratedAt || b.created || 0).getTime() -
            new Date(a.buildGeneratedAt || a.created || 0).getTime()
        ),
    [artifacts]
  );
  const containerSourcesForKind = (kind: 'api' | 'web') =>
    containerSources.filter(({ artifact }) =>
      containerMatchesPlannerKind(artifact, kind)
    );
  const campaignEnvironments = useMemo(
    () => [
      ...new Set([
        'dev',
        'staging',
        'production',
        ...workspace.deliveryCandidates.map(
          (candidate) => candidate.environment
        ),
        ...channels.map((channel) => channel.environment)
      ])
    ],
    [channels, workspace.deliveryCandidates]
  );
  const workJourneyOptions = useMemo(
    () =>
      searchCampaignWorkJourneys(
        workspace.workJourneys,
        workJourneySearch,
        workspace.apps
      ),
    [workJourneySearch, workspace.apps, workspace.workJourneys]
  );
  const liveRows = useMemo(() => {
    const containers = currentContainerSources.map((source) => {
      const runtime = summarizeRuntime(
        source.artifact.workloads || [],
        source.candidate,
        runtimeObservations
      );
      const runtimeImage = summarizeRuntimeImage(
        source.artifact.workloads || [],
        source.candidate,
        source.artifact,
        runtimeObservations
      );
      const projectIds = new Set(
        [source.candidate.projectId, ...source.candidate.projectIds].filter(
          Boolean
        )
      );
      const observedAt = runtimeObservations
        .filter(
          (item) =>
            projectIds.has(item.projectId) &&
            item.environment === source.candidate.environment &&
            (source.artifact.workloads || []).some(
              (workload) =>
                (workload.split('/').pop() || workload) === item.workload
            )
        )
        .map((item) => item.receivedAt || item.observedAt)
        .sort(
          (left, right) => new Date(right).getTime() - new Date(left).getTime()
        )[0];
      return {
        key: source.ref,
        environment: source.candidate.environment,
        project:
          workspace.apps.find((app) => app.id === source.candidate.projectId)
            ?.name || source.candidate.externalRepo,
        unit: source.artifact.label,
        version:
          source.artifact.tag || source.artifact.artifactId || 'Exact digest',
        identity: exactContainerIdentity(source.artifact),
        destination: 'Kubernetes',
        status: deliveryCandidateStatusLabel(source.candidate.status),
        tone: source.candidate.status,
        ...runtime,
        ...runtimeImage,
        workloads: source.artifact.workloads || [],
        url: source.candidate.url,
        updated: source.candidate.deployedAt || source.candidate.updated,
        observedAt
      };
    });
    const ota = channels
      .filter((channel) => channel.isActive)
      .flatMap((channel) => {
        const artifact = artifacts.find(
          (item) => item.releaseArtifactId === channel.releaseArtifactId
        );
        if (!artifact) return [];
        const version = appVersions.find(
          (item) => item.releaseAppVersionId === channel.releaseAppVersionId
        );
        const releaseApp = releaseApps.find(
          (item) => item.releaseAppId === channel.releaseAppId
        );
        const platform =
          version?.platform ||
          (artifact.platform === 'ios' || artifact.platform === 'android'
            ? artifact.platform
            : undefined);
        const platformLabel =
          platform === 'ios'
            ? 'iOS'
            : platform === 'android'
              ? 'Android'
              : 'OTA';
        const destinationParts = [
          releaseApp?.name || platformLabel,
          channel.channel
        ].filter(
          (part, index, values) => part && values.indexOf(part) === index
        );
        return [
          {
            key: `channel:${channel.releaseChannelId}`,
            environment: channel.environment,
            project:
              workspace.apps.find((app) => app.id === channel.projectId)
                ?.name || 'Project',
            unit: `${platformLabel} update`,
            version: formatArtifactSummary(artifact),
            identity: artifact.gitSha || artifact.releaseArtifactId,
            destination: destinationParts.join(' · '),
            status: 'Assigned',
            tone: 'deployed',
            runtimeStatus: 'Not applicable',
            runtimeDetail: undefined,
            runtimeTone: 'unknown',
            imageStatus: 'Not applicable',
            imageDetail: undefined,
            imageTone: 'unknown',
            workloads: [],
            url: undefined,
            updated: channel.updated,
            observedAt: undefined
          }
        ];
      });
    return [...containers, ...ota].sort(
      (a, b) =>
        environmentRank(a.environment) - environmentRank(b.environment) ||
        a.environment.localeCompare(b.environment) ||
        a.project.localeCompare(b.project)
    );
  }, [
    appVersions,
    artifacts,
    channels,
    currentContainerSources,
    releaseApps,
    runtimeObservations,
    workspace.apps
  ]);

  const liveEnvironments = useMemo(
    () =>
      [...new Set(liveRows.map((row) => row.environment))].sort(
        (left, right) =>
          environmentRank(left) - environmentRank(right) ||
          left.localeCompare(right)
      ),
    [liveRows]
  );
  const visibleLiveRows = useMemo(
    () =>
      environmentFilter === 'all'
        ? liveRows
        : liveRows.filter((row) => row.environment === environmentFilter),
    [environmentFilter, liveRows]
  );
  const kubernetesRows = liveRows.filter(
    (row) => row.destination === 'Kubernetes'
  );
  const verifiedRuntimeCount = kubernetesRows.filter(
    (row) => row.runtimeTone === 'deployed'
  ).length;
  const runtimeAttentionCount = kubernetesRows.filter(
    (row) => row.runtimeTone === 'failed' || row.runtimeTone === 'unknown'
  ).length;
  const activeStoreReviews = appVersions
    .filter((version) =>
      ['submitted', 'in_review', 'rejected', 'approved'].includes(
        version.storeReviewStatus || ''
      )
    )
    .map((version) => {
      const app = releaseApps.find(
        (candidate) => candidate.releaseAppId === version.releaseAppId
      );
      const config = workspace.runtimeConfigs.find(
        (candidate) =>
          candidate.runtimeConfigId === version.storeReviewRuntimeConfigId
      );
      const otaValue = config?.values?.find(
        (entry) => entry.key === 'otaUpdatesEnabled'
      )?.value;
      return {
        version,
        app,
        config,
        reviewRouting:
          version.storeReviewRoutingEnabled ??
          (config?.isActive === true && otaValue?.toLowerCase() === 'false')
      };
    });

  function addTarget(kind: PlannerKind) {
    const index =
      draftTargets.filter((target) => target.kind === kind).length + 1;
    const firstContainer =
      kind === 'api' || kind === 'web'
        ? containerSourcesForKind(kind)[0]
        : undefined;
    const firstOta = otaArtifacts[0];
    setDraftTargets((current) => [
      ...current,
      {
        key: `${kind}-${Date.now()}-${index}`,
        kind,
        sourceRef:
          kind === 'api' || kind === 'web'
            ? firstContainer?.ref || ''
            : firstOta
              ? `artifact:${firstOta.releaseArtifactId}`
              : '',
        environment:
          kind === 'api' || kind === 'web'
            ? firstContainer?.candidate.environment || 'production'
            : 'production',
        channel: '',
        dependencyKeys: [],
        approvalRequired: false
      }
    ]);
    setView('plan');
  }
  function updateTarget(key: string, patch: Partial<DraftTarget>) {
    setDraftTargets((current) =>
      current.map((target) =>
        target.key === key ? { ...target, ...patch } : target
      )
    );
  }
  function selectSource(target: DraftTarget, sourceRef: string) {
    const source = containerSources.find(
      (candidate) => candidate.ref === sourceRef
    );
    updateTarget(target.key, {
      sourceRef,
      environment: source?.candidate.environment || target.environment,
      channel: ''
    });
  }
  function availableChannels(target: DraftTarget) {
    return channels.filter((channel) => {
      const version = appVersions.find(
        (candidate) =>
          candidate.releaseAppVersionId === channel.releaseAppVersionId
      );
      return (
        channel.isActive &&
        channel.environment === target.environment &&
        version?.platform === target.kind
      );
    });
  }

  async function refreshRuntimeEvidence() {
    if (!session || !activeOrganization) return;
    const [connections, observations] = await Promise.all([
      listRuntimeObservers(session.accessToken, activeOrganization.id),
      listRuntimeObservations(session.accessToken, {
        organizationId: activeOrganization.id
      })
    ]);
    setRuntimeObservers(connections);
    setRuntimeObservations(observations);
  }

  async function issueObserver() {
    if (!session || !activeOrganization) return;
    if (
      !observerDraft.projectId ||
      !observerDraft.name.trim() ||
      !observerDraft.namespace.trim()
    ) {
      setError('Choose a project and provide the exact Kubernetes namespace.');
      return;
    }
    try {
      setBusy('runtime-create');
      setError('');
      const issued = await createRuntimeObserver(session.accessToken, {
        organizationId: activeOrganization.id,
        ...observerDraft,
        name: observerDraft.name.trim(),
        namespace: observerDraft.namespace.trim()
      });
      setIssuedObserver(issued);
      await refreshRuntimeEvidence();
    } catch (caught) {
      setError(
        requestErrorMessage(caught, 'Could not create the runtime observer.')
      );
    } finally {
      setBusy('');
    }
  }

  async function rotateObserver(connection: RuntimeObserverConnectionRow) {
    if (
      !session ||
      !window.confirm(
        `Rotate ${connection.name}? The currently installed token will stop working immediately.`
      )
    )
      return;
    try {
      setBusy(`runtime-rotate:${connection.runtimeObserverConnectionId}`);
      setError('');
      setIssuedObserver(
        await rotateRuntimeObserver(
          session.accessToken,
          connection.runtimeObserverConnectionId
        )
      );
      setShowRuntimeSetup(true);
      await refreshRuntimeEvidence();
    } catch (caught) {
      setError(
        requestErrorMessage(caught, 'Could not rotate the observer credential.')
      );
    } finally {
      setBusy('');
    }
  }

  async function revokeObserver(connection: RuntimeObserverConnectionRow) {
    if (
      !session ||
      !window.confirm(
        `Revoke ${connection.name}? Runtime evidence from this observer will stop immediately.`
      )
    )
      return;
    try {
      setBusy(`runtime-revoke:${connection.runtimeObserverConnectionId}`);
      setError('');
      await revokeRuntimeObserver(
        session.accessToken,
        connection.runtimeObserverConnectionId
      );
      if (
        issuedObserver?.runtimeObserverConnectionId ===
        connection.runtimeObserverConnectionId
      )
        setIssuedObserver(null);
      await refreshRuntimeEvidence();
    } catch (caught) {
      setError(
        requestErrorMessage(caught, 'Could not revoke the observer credential.')
      );
    } finally {
      setBusy('');
    }
  }

  async function createCampaign() {
    if (!session || !activeOrganization) return;
    if (!name.trim() || !draftTargets.length) {
      setError('Name the release and add at least one release unit.');
      return;
    }
    try {
      const targets = draftTargets.map((target, position) => {
        const copy = kindCopy[target.kind];
        const dependencies = target.dependencyKeys.map(
          (key) =>
            `target-${draftTargets.findIndex((item) => item.key === key) + 1}`
        );
        if (target.sourceRef.startsWith('candidate:')) {
          const source = containerSources.find(
            (item) => item.ref === target.sourceRef
          );
          if (!source)
            throw new Error(`${copy.label} needs an exact delivery candidate.`);
          return {
            targetKey: `target-${position + 1}`,
            releaseUnitKind: 'container' as const,
            label: `${copy.label} · ${source.artifact.label}`,
            projectId:
              source.candidate.projectId || source.candidate.projectIds[0],
            sourceType: 'delivery-candidate' as const,
            sourceId: source.candidate.deliveryCandidateId,
            sourceArtifactKey: source.artifact.key,
            artifactIdentity: exactContainerIdentity(source.artifact),
            artifactLabel: source.artifact.label,
            platform: target.kind === 'web' ? ('web' as const) : undefined,
            environment: source.candidate.environment,
            destinationType: 'kubernetes' as const,
            destinationLabel: `${source.candidate.environment} Kubernetes`,
            dependencyKeys: dependencies,
            rolloutStrategy: target.approvalRequired
              ? ('manual' as const)
              : dependencies.length
                ? ('after-dependencies' as const)
                : ('parallel' as const),
            metadata: {
              providerUrl: source.candidate.url,
              repository: source.candidate.externalRepo
            }
          };
        }
        const artifact = artifacts.find(
          (item) =>
            item.releaseArtifactId ===
            target.sourceRef.replace(/^artifact:/, '')
        );
        if (!artifact || !target.channel)
          throw new Error(`${copy.label} needs an exact bundle and channel.`);
        return {
          targetKey: `target-${position + 1}`,
          releaseUnitKind: 'ota' as const,
          label: `${copy.label} · ${target.channel}`,
          projectId: artifact.projectId,
          sourceType: 'release-artifact' as const,
          sourceId: artifact.releaseArtifactId,
          artifactIdentity: artifact.gitSha || artifact.releaseArtifactId,
          artifactLabel: formatArtifactSummary(artifact),
          platform: target.kind as 'ios' | 'android',
          releaseAppVersionId: channels.find(
            (channel) =>
              channel.environment === target.environment &&
              channel.channel === target.channel &&
              appVersions.find(
                (version) =>
                  version.releaseAppVersionId === channel.releaseAppVersionId
              )?.platform === target.kind
          )?.releaseAppVersionId,
          environment: target.environment,
          destinationType: 'ota-channel' as const,
          destinationLabel: `${target.environment} / ${target.channel}`,
          channel: target.channel,
          dependencyKeys: dependencies,
          rolloutStrategy: target.approvalRequired
            ? ('manual' as const)
            : dependencies.length
              ? ('after-dependencies' as const)
              : ('parallel' as const),
          metadata: {}
        };
      });
      setBusy('create');
      setError('');
      await createDeliveryCampaign(session.accessToken, {
        organizationId: activeOrganization.id,
        name: name.trim(),
        summary: summary.trim() || undefined,
        workJourneyIds: selectedWorkJourneyIds,
        targets
      });
      setName('');
      setSummary('');
      setDraftTargets([]);
      setSelectedWorkJourneyIds([]);
      setWorkJourneySearch('');
      refreshWorkspace();
      setView('campaigns');
    } catch (caught) {
      setError(requestErrorMessage(caught, 'Could not save this release.'));
    } finally {
      setBusy('');
    }
  }
  async function startCampaign(campaign: DeliveryCampaignRow) {
    if (!session) return;
    setBusy(campaign.deliveryCampaignId);
    setError('');
    try {
      await startDeliveryCampaign(
        session.accessToken,
        campaign.deliveryCampaignId
      );
      refreshWorkspace();
    } catch (caught) {
      setError(
        requestErrorMessage(caught, 'Could not move this release forward.')
      );
    } finally {
      setBusy('');
    }
  }
  async function cancelCampaign(campaign: DeliveryCampaignRow) {
    if (!session) return;
    const accepted = await confirm({
      title: 'Cancel this planned release?',
      message:
        'No deployment has started. Its targets will stay untouched, and this plan cannot be restarted. Create a new plan if you want to ship these changes later.',
      confirmLabel: 'Cancel release',
      danger: true
    });
    if (!accepted) return;
    setBusy(`cancel:${campaign.deliveryCampaignId}`);
    setError('');
    try {
      await cancelDeliveryCampaign(
        session.accessToken,
        campaign.deliveryCampaignId
      );
      toast('success', 'Planned release cancelled');
      refreshWorkspace();
    } catch (caught) {
      setError(requestErrorMessage(caught, 'Could not cancel this release.'));
    } finally {
      setBusy('');
    }
  }
  async function runPreflight(campaign: DeliveryCampaignRow) {
    if (!session) return;
    setBusy(`preflight:${campaign.deliveryCampaignId}`);
    setError('');
    try {
      await preflightDeliveryCampaign(
        session.accessToken,
        campaign.deliveryCampaignId
      );
      setExpandedCampaignId(campaign.deliveryCampaignId);
      refreshWorkspace();
    } catch (caught) {
      setError(requestErrorMessage(caught, 'Could not check this release.'));
    } finally {
      setBusy('');
    }
  }

  async function retryCampaignTarget(
    campaign: DeliveryCampaignRow,
    target: DeliveryCampaignRow['targets'][number]
  ) {
    if (!session) return;
    const retryKey = `retry:${campaign.deliveryCampaignId}:${target.targetKey}`;
    setBusy(retryKey);
    setError('');
    try {
      await retryDeliveryCampaignTarget(
        session.accessToken,
        campaign.deliveryCampaignId,
        target.targetKey
      );
      setExpandedCampaignId(campaign.deliveryCampaignId);
      await refreshWorkspace();
    } catch (caught) {
      setError(requestErrorMessage(caught, `Could not retry ${target.label}.`));
    } finally {
      setBusy('');
    }
  }

  async function approveCampaignTarget(
    campaign: DeliveryCampaignRow,
    target: DeliveryCampaignRow['targets'][number]
  ) {
    if (!session) return;
    const approvalKey = `approve:${campaign.deliveryCampaignId}:${target.targetKey}`;
    setBusy(approvalKey);
    setError('');
    try {
      await approveDeliveryCampaignTarget(
        session.accessToken,
        campaign.deliveryCampaignId,
        target.targetKey
      );
      setExpandedCampaignId(campaign.deliveryCampaignId);
      await refreshWorkspace();
    } catch (caught) {
      setError(
        requestErrorMessage(caught, `Could not approve ${target.label}.`)
      );
    } finally {
      setBusy('');
    }
  }

  const activeRuntimeObservers = runtimeObservers.filter(
    (item) => item.status === 'active'
  );
  const runtimeConnected = activeRuntimeObservers.some(
    (item) =>
      item.lastSeenAt &&
      Date.now() - new Date(item.lastSeenAt).getTime() < 5 * 60_000
  );

  return (
    <div className="releaseControlPage">
      <div className="releaseControlToolbar">
        <nav className="releaseControlTabs" aria-label="Release control views">
          {(
            [
              ['state', 'Live releases'],
              ['campaigns', `Releases · ${workspace.deliveryCampaigns.length}`],
              ['plan', 'Plan a release']
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={view === id ? 'active' : ''}
              onClick={() => setView(id)}
            >
              {label}
            </button>
          ))}
        </nav>
        {view !== 'plan' ? (
          <button
            type="button"
            className="primaryButton releaseControlPlanButton"
            onClick={() => setView('plan')}
          >
            <Plus size={16} /> Plan a release
          </button>
        ) : null}
      </div>
      {error ? (
        <div className="releaseControlError">
          <AlertCircle size={17} />
          {error}
        </div>
      ) : null}

      {view === 'state' ? (
        <section className="releaseStateSection">
          <SectionHeading
            eyebrow="Live releases"
            title="What is deployed now"
            description="One current record per release unit, with deployment history and live workload evidence kept separate."
            count={`${liveRows.length} release units`}
          />
          {activeStoreReviews.length ? (
            <section
              className="activeStoreReviews"
              aria-label="Active store reviews"
            >
              <header>
                <div>
                  <span>Store review</span>
                  <strong>
                    {activeStoreReviews.length === 1
                      ? 'One build needs watching'
                      : `${activeStoreReviews.length} builds need watching`}
                  </strong>
                </div>
                <small>
                  Store status and client routing are shown separately.
                </small>
              </header>
              <div>
                {activeStoreReviews.map(
                  ({ version, app, config, reviewRouting }) => (
                    <article key={version.releaseAppVersionId}>
                      <span
                        className={`releaseStateBadge releaseStateBadge--${version.storeReviewStatus === 'rejected' ? 'failed' : version.storeReviewStatus === 'approved' ? 'deployed' : 'deploying'}`}
                      >
                        <CircleDot size={12} />
                        {storeReviewLabel(version.storeReviewStatus)}
                      </span>
                      <div>
                        <strong>
                          {app?.name || version.platform.toUpperCase()}{' '}
                          {version.version}
                          {version.storeReviewBuildNumber
                            ? ` (${version.storeReviewBuildNumber})`
                            : ''}
                        </strong>
                        <small>
                          {reviewRouting
                            ? `Reviewers use ${config?.environment}; OTA held`
                            : version.storeReviewStatus === 'approved'
                              ? 'Approved; waiting for production routing'
                              : 'Review routing is not active'}
                        </small>
                      </div>
                    </article>
                  )
                )}
              </div>
            </section>
          ) : null}
          <div
            className="releaseTruthSummary"
            aria-label="Release state summary"
          >
            <div>
              <span>Current release units</span>
              <strong>{liveRows.length}</strong>
              <small>Latest deployed record only</small>
            </div>
            <div className="releaseTruthSummary--ready">
              <span>Runtime confirmed</span>
              <strong>{verifiedRuntimeCount}</strong>
              <small>Every mapped workload is ready</small>
            </div>
            <div
              className={
                runtimeAttentionCount ? 'releaseTruthSummary--attention' : ''
              }
            >
              <span>Missing or unhealthy</span>
              <strong>{runtimeAttentionCount}</strong>
              <small>
                {runtimeAttentionCount
                  ? 'Runtime evidence needs attention'
                  : 'No runtime gaps detected'}
              </small>
            </div>
            <div>
              <span>Environments</span>
              <strong>{liveEnvironments.length}</strong>
              <small>{liveEnvironments.join(' · ') || 'No releases yet'}</small>
            </div>
          </div>
          <div
            className={`releaseEvidenceNotice ${runtimeConnected ? 'releaseEvidenceNotice--connected' : ''}`}
          >
            {runtimeConnected ? (
              <ShieldCheck size={17} />
            ) : (
              <Unplug size={17} />
            )}
            <div>
              <strong>
                {runtimeConnected
                  ? 'Live workload checks are arriving'
                  : activeRuntimeObservers.length
                    ? 'No recent workload check'
                    : 'Live workload checks are not connected'}
              </strong>
              <span>
                {runtimeConnected
                  ? `${runtimeObservations.filter((item) => item.freshness === 'fresh').length} fresh workload records from Prometheus. A green deployment means the delivery record completed. Runtime is only green when every mapped workload is ready.`
                  : activeRuntimeObservers.length
                    ? 'The connection exists, but Cindro has not received a fresh snapshot in the last five minutes. Check the observer pod and Prometheus endpoint.'
                    : 'Connect the outbound observer to see pod readiness, replicas and rollout state. Until then, Cindro will leave runtime health unknown.'}
              </span>
            </div>
            <button
              type="button"
              className="secondaryButton"
              onClick={() => {
                setShowRuntimeSetup((current) => !current);
                if (!observerDraft.projectId && workspace.apps[0])
                  setObserverDraft((current) => ({
                    ...current,
                    projectId: workspace.apps[0].id
                  }));
              }}
            >
              <KeyRound size={15} />
              {showRuntimeSetup
                ? 'Close setup'
                : activeRuntimeObservers.length
                  ? 'Manage connection'
                  : 'Connect Prometheus'}
            </button>
          </div>
          {showRuntimeSetup ? (
            <RuntimeObserverSetup
              projects={workspace.apps}
              connections={runtimeObservers}
              draft={observerDraft}
              setDraft={setObserverDraft}
              issued={issuedObserver}
              busy={busy}
              onIssue={issueObserver}
              onRotate={rotateObserver}
              onRevoke={revokeObserver}
            />
          ) : null}
          {liveRows.length === 0 ? (
            <Empty
              icon={<Layers3 size={22} />}
              title="No deployed releases yet"
              body="A completed GitOps deployment or an assigned OTA bundle will appear here."
            />
          ) : (
            <>
              <div
                className="releaseEnvironmentFilter"
                aria-label="Filter releases by environment"
              >
                <button
                  type="button"
                  className={environmentFilter === 'all' ? 'active' : ''}
                  onClick={() => setEnvironmentFilter('all')}
                >
                  All environments <span>{liveRows.length}</span>
                </button>
                {liveEnvironments.map((environment) => (
                  <button
                    type="button"
                    key={environment}
                    className={
                      environmentFilter === environment ? 'active' : ''
                    }
                    onClick={() => setEnvironmentFilter(environment)}
                  >
                    {environment}{' '}
                    <span>
                      {
                        liveRows.filter(
                          (row) => row.environment === environment
                        ).length
                      }
                    </span>
                  </button>
                ))}
              </div>
              <div className="releaseControlTableWrap">
                <table className="releaseControlTable">
                  <thead>
                    <tr>
                      <th>Release unit</th>
                      <th>Environment</th>
                      <th>Selected build</th>
                      <th>Destination</th>
                      <th>Delivery record</th>
                      <th>Live workload check</th>
                      <th>Evidence time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLiveRows.map((row) => (
                      <tr key={row.key}>
                        <td>
                          <span className="releaseUnitCell">
                            <strong>{row.unit}</strong>
                            <span>{row.project}</span>
                          </span>
                        </td>
                        <td>
                          <strong>{row.environment}</strong>
                        </td>
                        <td>
                          <span className="releaseVersionCell">
                            <span title={row.version}>{row.version}</span>
                            <code title={row.identity}>
                              {shortIdentity(row.identity)}
                            </code>
                            <small
                              className={`releaseImageEvidence releaseImageEvidence--${row.imageTone}`}
                              title={row.imageDetail}
                            >
                              {row.imageStatus}
                            </small>
                          </span>
                        </td>
                        <td>
                          <span className="releaseWorkloadCell">
                            <strong>{row.destination}</strong>
                            {row.workloads.length ? (
                              <span title={row.workloads.join(', ')}>
                                {row.workloads.length} workload
                                {row.workloads.length === 1 ? '' : 's'} ·{' '}
                                {row.workloads.slice(0, 2).join(', ')}
                                {row.workloads.length > 2
                                  ? ` +${row.workloads.length - 2}`
                                  : ''}
                              </span>
                            ) : row.destination === 'Kubernetes' ? (
                              <span>No workload mapping</span>
                            ) : null}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`releaseStateBadge releaseStateBadge--${row.tone}`}
                          >
                            <CircleDot size={12} />
                            {row.status}
                          </span>
                          {row.url ? (
                            <a
                              className="releaseEvidenceLink"
                              href={row.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <GitPullRequest size={13} />
                              Open deployment
                              <ExternalLink size={11} />
                            </a>
                          ) : null}
                        </td>
                        <td>
                          <span className="releaseRuntimeCheck">
                            <span
                              className={`releaseStateBadge releaseStateBadge--${row.runtimeTone}`}
                            >
                              <CircleDot size={12} />
                              {row.runtimeStatus}
                            </span>
                            {row.runtimeDetail ? (
                              <small>{row.runtimeDetail}</small>
                            ) : null}
                          </span>
                        </td>
                        <td>
                          <span className="releaseEvidenceTime">
                            <strong>
                              {formatRelativeDate(
                                row.observedAt || row.updated
                              )}
                            </strong>
                            <small>
                              {row.observedAt
                                ? 'Runtime observed'
                                : 'Delivery recorded'}
                            </small>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      ) : null}

      {view === 'plan' ? (
        <section className="campaignPlanner">
          <SectionHeading
            eyebrow="New release"
            title="Choose what ships together"
            description="Add the API, web and app updates for this release. Each one can use its own build, destination and start condition."
            count={`${draftTargets.length} release units`}
          />
          <div className="campaignIdentityFields">
            <label>
              <span>Release name</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Customer release · 24 August"
              />
            </label>
            <label>
              <span>
                What is changing? <small>optional</small>
              </span>
              <input
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
                placeholder="A short note for the people shipping it"
              />
            </label>
          </div>
          <details className="campaignWorkSelection">
            <summary>
              <span>Related work</span>
              <b>
                {selectedWorkJourneyIds.length
                  ? `${selectedWorkJourneyIds.length} linked`
                  : 'Add tasks and pull requests'}
              </b>
              <ChevronDown size={15} />
            </summary>
            <div className="campaignWorkSelectionBody">
              <p>
                Link the work journeys that belong in this release. Cindro
                records the connection and leaves the tasks and pull requests
                unchanged.
              </p>
              <label className="campaignWorkSearch">
                <span className="srOnly">Search work journeys</span>
                <input
                  type="search"
                  value={workJourneySearch}
                  onChange={(event) => setWorkJourneySearch(event.target.value)}
                  placeholder="Search by task, pull request, repo or project"
                />
              </label>
              {!workspace.workJourneys.length ? (
                <div className="campaignWorkEmpty">
                  No work journeys are available in this workspace yet.
                </div>
              ) : !workJourneyOptions.length ? (
                <div className="campaignWorkEmpty">
                  No work matches that search.
                </div>
              ) : (
                <div className="campaignWorkOptions">
                  {workJourneyOptions.map((journey) => {
                    const checked = selectedWorkJourneyIds.includes(
                      journey.workJourneyId
                    );
                    const projectNames = workspace.apps
                      .filter((app) => journey.projectIds.includes(app.id))
                      .map((app) => app.name);
                    const workRefs = journey.taskRefs.length
                      ? journey.taskRefs.join(', ')
                      : `${journey.items.length} tracked items`;
                    return (
                      <label
                        className="campaignWorkOption"
                        key={journey.workJourneyId}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) =>
                            setSelectedWorkJourneyIds((current) =>
                              event.target.checked
                                ? [...current, journey.workJourneyId]
                                : current.filter(
                                    (id) => id !== journey.workJourneyId
                                  )
                            )
                          }
                        />
                        <span>
                          <strong>{journey.title}</strong>
                          <small>
                            {workRefs} ·{' '}
                            {projectNames.join(', ') ||
                              'Project not identified'}{' '}
                            · {journey.status}
                          </small>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </details>
          <div className="campaignAddBar">
            <span>Add a release unit</span>
            {(['api', 'web', 'ios', 'android'] as const).map((kind) => (
              <button key={kind} type="button" onClick={() => addTarget(kind)}>
                {targetIcon(kind)}
                {kindCopy[kind].label}
                <Plus size={13} />
              </button>
            ))}
          </div>
          {!draftTargets.length ? (
            <Empty
              icon={<PackageCheck size={22} />}
              title="Nothing selected yet"
              body="Add the API, web, iOS or Android updates that should ship in this release. You can add the same platform more than once for different versions or channels."
            />
          ) : (
            <div className="campaignTargetList">
              {draftTargets.map((target, index) => {
                const container =
                  target.kind === 'api' || target.kind === 'web';
                const source = containerSources.find(
                  (item) => item.ref === target.sourceRef
                );
                const eligibleContainerSources = container
                  ? containerSourcesForKind(target.kind as 'api' | 'web')
                  : [];
                return (
                  <article className="campaignTargetRow" key={target.key}>
                    <div className="campaignTargetOrdinal">
                      <span>{index + 1}</span>
                      {targetIcon(target.kind)}
                    </div>
                    <div className="campaignTargetFields">
                      <div className="campaignTargetTitle">
                        <div>
                          <strong>{kindCopy[target.kind].label}</strong>
                          <span>{kindCopy[target.kind].description}</span>
                        </div>
                        <button
                          type="button"
                          aria-label={`Remove ${kindCopy[target.kind].label}`}
                          onClick={() =>
                            setDraftTargets((current) =>
                              current.filter((item) => item.key !== target.key)
                            )
                          }
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <div className="campaignTargetGrid">
                        <label>
                          <span>Build or image</span>
                          <select
                            value={target.sourceRef}
                            onChange={(event) =>
                              selectSource(target, event.target.value)
                            }
                          >
                            <option value="">Choose an artifact</option>
                            {container
                              ? eligibleContainerSources.map((item) => (
                                  <option key={item.ref} value={item.ref}>
                                    {item.artifact.label} ·{' '}
                                    {item.candidate.environment} ·{' '}
                                    {shortIdentity(
                                      exactContainerIdentity(item.artifact)
                                    )}
                                  </option>
                                ))
                              : otaArtifacts.map((artifact) => (
                                  <option
                                    key={artifact.releaseArtifactId}
                                    value={`artifact:${artifact.releaseArtifactId}`}
                                  >
                                    {formatArtifactSummary(artifact)}
                                  </option>
                                ))}
                          </select>
                        </label>
                        <label>
                          <span>Environment</span>
                          {container ? (
                            <input
                              value={
                                source?.candidate.environment ||
                                target.environment
                              }
                              readOnly
                            />
                          ) : (
                            <select
                              value={target.environment}
                              onChange={(event) =>
                                updateTarget(target.key, {
                                  environment: event.target.value,
                                  channel: ''
                                })
                              }
                            >
                              {campaignEnvironments.map((environment) => (
                                <option key={environment} value={environment}>
                                  {environment}
                                </option>
                              ))}
                            </select>
                          )}
                        </label>
                        <label>
                          <span>Destination</span>
                          {container ? (
                            <input
                              value="Kubernetes via the deployment repository"
                              readOnly
                            />
                          ) : (
                            <select
                              value={target.channel}
                              onChange={(event) =>
                                updateTarget(target.key, {
                                  channel: event.target.value
                                })
                              }
                            >
                              <option value="">Choose a channel</option>
                              {availableChannels(target).map((channel) => (
                                <option
                                  key={channel.releaseChannelId}
                                  value={channel.channel}
                                >
                                  {channel.channel}
                                </option>
                              ))}
                            </select>
                          )}
                        </label>
                      </div>
                      <details className="campaignDependencies">
                        <summary>
                          <ChevronDown size={14} /> Starts{' '}
                          {target.approvalRequired
                            ? 'after approval'
                            : target.dependencyKeys.length
                              ? `after ${target.dependencyKeys.length} target${target.dependencyKeys.length === 1 ? '' : 's'}`
                              : 'in parallel'}
                        </summary>
                        <div>
                          <label>
                            <input
                              type="checkbox"
                              checked={target.approvalRequired}
                              onChange={(event) =>
                                updateTarget(target.key, {
                                  approvalRequired: event.target.checked
                                })
                              }
                            />
                            Require approval before this target can move
                          </label>
                          <span>Only advance this target after:</span>
                          {draftTargets
                            .filter((item) => item.key !== target.key)
                            .map((dependency) => (
                              <label key={dependency.key}>
                                <input
                                  type="checkbox"
                                  checked={target.dependencyKeys.includes(
                                    dependency.key
                                  )}
                                  onChange={(event) =>
                                    updateTarget(target.key, {
                                      dependencyKeys: event.target.checked
                                        ? [
                                            ...target.dependencyKeys,
                                            dependency.key
                                          ]
                                        : target.dependencyKeys.filter(
                                            (key) => key !== dependency.key
                                          )
                                    })
                                  }
                                />
                                {kindCopy[dependency.kind].label}
                              </label>
                            ))}
                        </div>
                      </details>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          <footer className="campaignPlannerFooter">
            <div>
              <strong>Review before anything runs</strong>
              <span>
                Saving this release does not deploy it. You will check the
                builds and destinations before starting.
              </span>
            </div>
            <button
              type="button"
              className="primaryButton"
              disabled={busy === 'create' || !draftTargets.length}
              onClick={createCampaign}
            >
              {busy === 'create' ? (
                <RefreshCw className="spin" size={16} />
              ) : (
                <PackageCheck size={16} />
              )}{' '}
              Save release
            </button>
          </footer>
        </section>
      ) : null}

      {view === 'campaigns' ? (
        <section className="campaignLedger">
          <SectionHeading
            eyebrow="Releases"
            title="Ready, running and completed"
            description="See what can move, what is blocked and what has reached each destination."
            count={`${workspace.deliveryCampaigns.length} total`}
          />
          {!workspace.deliveryCampaigns.length ? (
            <Empty
              icon={<Rocket size={22} />}
              title="No coordinated releases yet"
              body="Plan one release containing every API, web and app update that should ship together."
            />
          ) : (
            <div className="campaignRows">
              {workspace.deliveryCampaigns.map((campaign) => {
                const complete = campaign.targets.filter(
                  (target) =>
                    target.status === 'released' || target.status === 'observed'
                ).length;
                const progress = describeCampaignProgress(
                  campaign.targets,
                  campaign.status
                );
                const expanded =
                  expandedCampaignId === campaign.deliveryCampaignId;
                const preflight = campaign.preflight || {
                  status: 'unknown' as const,
                  ready: 0,
                  blocked: 0,
                  unknown: 0,
                  total: 0
                };
                const readyTargetKeys = readyCampaignTargetKeys(campaign);
                const remainingTargetCount = campaign.targets.filter(
                  (target) =>
                    target.status !== 'released' && target.status !== 'observed'
                ).length;
                const blockers = (campaign.gates || []).filter(
                  (gate) => gate.status !== 'ready'
                );
                const campaignBlockers = campaignWideGateIssues(campaign);
                const targetBlockerCount =
                  blockers.length - campaignBlockers.length;
                const lastCheckedAt = [...(campaign.gates || [])].sort(
                  (left, right) =>
                    new Date(right.evaluatedAt).getTime() -
                    new Date(left.evaluatedAt).getTime()
                )[0]?.evaluatedAt;
                const checkedWhen = lastCheckedAt
                  ? formatRelativeDate(lastCheckedAt)
                  : undefined;
                const decisionTitle =
                  campaign.status === 'completed'
                    ? 'Every release unit has a completed delivery step'
                    : readyTargetKeys.length
                      ? campaign.status === 'planned'
                        ? `${readyTargetKeys.length} target${readyTargetKeys.length === 1 ? '' : 's'} can start`
                        : `${readyTargetKeys.length} target${readyTargetKeys.length === 1 ? '' : 's'} can move`
                      : preflight.status === 'ready'
                        ? campaign.status === 'planned'
                          ? 'Ready to start'
                          : 'Ready for the next step'
                        : preflight.status === 'blocked'
                          ? 'Fix these blockers before starting'
                          : 'Check this release before starting';
                const decisionCopy =
                  campaign.status === 'completed' &&
                  preflight.status === 'ready'
                    ? `${preflight.ready} checks passed before or during this release${checkedWhen ? `; last checked ${checkedWhen}` : ''}.`
                    : readyTargetKeys.length && preflight.status !== 'ready'
                      ? `Cindro will move these ${readyTargetKeys.length} target${readyTargetKeys.length === 1 ? '' : 's'} only. The others will stay put until their checks and prerequisites pass.`
                      : preflight.status === 'ready'
                        ? `${preflight.ready} checks passed${checkedWhen ? ` ${checkedWhen}` : ''} for the selected builds, destinations and ownership.`
                        : blockers[0]?.resolution ||
                          blockers[0]?.detail ||
                          'Cindro has not checked the current builds and destinations yet.';
                return (
                  <article
                    className={`campaignRow campaignRow--${preflight.status}`}
                    key={campaign.deliveryCampaignId}
                  >
                    <div className="campaignRowSummary">
                      <div>
                        <div className="campaignHeadline">
                          <span
                            className={`campaignStatus campaignStatus--${campaign.status}`}
                          >
                            {statusLabel(campaign.status)}
                          </span>
                          <span
                            className={`campaignReadiness campaignReadiness--${preflight.status}`}
                          >
                            {preflight.status === 'ready' ? (
                              <ShieldCheck size={13} />
                            ) : (
                              <ShieldAlert size={13} />
                            )}
                            {preflight.status === 'ready'
                              ? checkedWhen
                                ? `Checked ${checkedWhen}`
                                : 'Checks passed'
                              : preflight.status === 'blocked'
                                ? `${preflight.blocked} blocked`
                                : 'Needs checking'}
                          </span>
                        </div>
                        <h3>{campaign.name}</h3>
                        <p>
                          {campaign.summary ||
                            `${campaign.targets.length} release units across ${new Set(campaign.targets.map((target) => target.environment)).size} environments.`}
                        </p>
                      </div>
                      <div className="campaignProgress">
                        <strong>
                          {complete}/{campaign.targets.length}
                        </strong>
                        <span>delivery steps complete</span>
                      </div>
                    </div>
                    <div
                      className="campaignRunbookPath"
                      aria-label="Release targets"
                    >
                      {campaign.targets.map((target) => (
                        <div
                          className={`campaignRunbookTarget campaignRunbookTarget--${target.status}`}
                          key={target.deliveryCampaignTargetId}
                        >
                          <span>
                            {target.releaseUnitKind === 'container' ? (
                              <Server size={15} />
                            ) : (
                              <Smartphone size={15} />
                            )}
                          </span>
                          <div>
                            <strong>{target.label}</strong>
                            <small>{target.destinationLabel}</small>
                          </div>
                          <small className="campaignRunbookStartCondition">
                            {describeCampaignStartCondition(
                              target,
                              campaign.targets
                            )}
                          </small>
                          <b>
                            {deliveryCampaignTargetStatusLabel(target.status)}
                          </b>
                        </div>
                      ))}
                    </div>
                    {campaign.workJourneyIds.length ? (
                      <div className="campaignRelatedWork">
                        <strong>Related work</strong>
                        <div>
                          {campaign.workJourneyIds.map((id) => {
                            const journey = workspace.workJourneys.find(
                              (item) => item.workJourneyId === id
                            );
                            return (
                              <span key={id}>
                                {journey && activeOrganization ? (
                                  <Link
                                    className="campaignRelatedWorkLink"
                                    to={buildWorkJourneyPath(
                                      activeOrganization.slug,
                                      id
                                    )}
                                    aria-label={`Open work journey ${journey.title}`}
                                  >
                                    <b>{journey.title}</b>
                                    <small>
                                      {journey.taskRefs.length
                                        ? journey.taskRefs.join(', ')
                                        : `${journey.items.length} tracked items`}
                                    </small>
                                  </Link>
                                ) : (
                                  <>
                                    <b>
                                      {journey?.title ||
                                        'Work journey unavailable'}
                                    </b>
                                    <small>
                                      {journey
                                        ? journey.taskRefs.length
                                          ? journey.taskRefs.join(', ')
                                          : `${journey.items.length} tracked items`
                                        : 'This journey is no longer in the loaded workspace'}
                                    </small>
                                  </>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                    {expanded ? (
                      <div className="campaignRunbookReview">
                        <div className="campaignDecision">
                          <span
                            className={`campaignDecisionIcon campaignDecisionIcon--${preflight.status}`}
                          >
                            {preflight.status === 'ready' ? (
                              <ShieldCheck size={20} />
                            ) : (
                              <ShieldAlert size={20} />
                            )}
                          </span>
                          <div>
                            <span className="eyebrow">
                              {campaign.status === 'completed'
                                ? 'Release result'
                                : 'What happens next'}
                            </span>
                            <strong>{decisionTitle}</strong>
                            <p>{decisionCopy}</p>
                          </div>
                        </div>
                        <div className="campaignRunbookGrid">
                          <section>
                            <header>
                              <span>What will ship</span>
                              <small>
                                {campaign.targets.length} release units
                              </small>
                            </header>
                            {campaign.targets.map((target) => {
                              const targetIssues = campaignTargetGateIssues(
                                campaign,
                                target
                              );
                              const approval =
                                target.metadata?.operatorApproval;
                              const prerequisitesDone =
                                target.dependencyKeys.every((key) =>
                                  campaign.targets.some(
                                    (candidate) =>
                                      candidate.targetKey === key &&
                                      ['released', 'observed'].includes(
                                        candidate.status
                                      )
                                  )
                                );
                              const targetGates = campaign.gates.filter(
                                (gate) =>
                                  gate.deliveryCampaignTargetId ===
                                  target.deliveryCampaignTargetId
                              );
                              const canApprove =
                                target.rolloutStrategy === 'manual' &&
                                !approval &&
                                ['planned', 'waiting'].includes(
                                  target.status
                                ) &&
                                targetGates.length > 0 &&
                                !targetIssues.length &&
                                prerequisitesDone;
                              return (
                                <div
                                  className={`campaignContractRow campaignContractRow--${target.status}`}
                                  key={target.deliveryCampaignTargetId}
                                >
                                  <span>
                                    {target.releaseUnitKind === 'container' ? (
                                      <Server size={15} />
                                    ) : (
                                      <Smartphone size={15} />
                                    )}
                                  </span>
                                  <div>
                                    <strong>{target.label}</strong>
                                    <small
                                      title={target.errorMessage || undefined}
                                    >
                                      {approval
                                        ? `Approved by ${approval.actorType === 'api_key' ? 'an API key' : 'a workspace user'} · ${formatRelativeDate(approval.approvedAt)}`
                                        : target.errorMessage ||
                                          target.artifactLabel}
                                    </small>
                                  </div>
                                  <div>
                                    <span>{target.environment}</span>
                                    <small>{target.destinationLabel}</small>
                                  </div>
                                  <code title={target.artifactIdentity}>
                                    {shortIdentity(target.artifactIdentity)}
                                  </code>
                                  {targetIssues.length ? (
                                    <div
                                      className="campaignTargetGateIssues"
                                      aria-label={`Checks affecting ${target.label}`}
                                    >
                                      {targetIssues.map((gate) => (
                                        <div
                                          className={`campaignTargetGateIssue campaignTargetGateIssue--${gate.status}`}
                                          key={gate.deliveryCampaignGateId}
                                        >
                                          <AlertCircle size={14} />
                                          <span>
                                            <strong>{gate.title}</strong>
                                            <small>
                                              {gate.resolution || gate.detail}
                                            </small>
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                  {canApprove ? (
                                    <button
                                      type="button"
                                      className="campaignTargetRetry"
                                      disabled={
                                        busy ===
                                        `approve:${campaign.deliveryCampaignId}:${target.targetKey}`
                                      }
                                      onClick={() =>
                                        approveCampaignTarget(campaign, target)
                                      }
                                    >
                                      {busy ===
                                      `approve:${campaign.deliveryCampaignId}:${target.targetKey}` ? (
                                        <RefreshCw className="spin" size={14} />
                                      ) : (
                                        <Check size={14} />
                                      )}
                                      Approve {target.label}
                                    </button>
                                  ) : null}
                                  {target.status === 'failed' ? (
                                    <button
                                      type="button"
                                      className="campaignTargetRetry"
                                      disabled={
                                        busy ===
                                        `retry:${campaign.deliveryCampaignId}:${target.targetKey}`
                                      }
                                      onClick={() =>
                                        retryCampaignTarget(campaign, target)
                                      }
                                    >
                                      {busy ===
                                      `retry:${campaign.deliveryCampaignId}:${target.targetKey}` ? (
                                        <RefreshCw className="spin" size={14} />
                                      ) : (
                                        <RefreshCw size={14} />
                                      )}
                                      Retry {target.label}
                                    </button>
                                  ) : null}
                                </div>
                              );
                            })}
                          </section>
                          <section>
                            <header>
                              <span>Checks before release</span>
                              <small>
                                {preflight.ready}/{preflight.total || '—'}{' '}
                                passed{checkedWhen ? ` · ${checkedWhen}` : ''}
                              </small>
                            </header>
                            {!(campaign.gates || []).length ? (
                              <div className="campaignNoChecks">
                                <ScanSearch size={18} />
                                <span>
                                  Check the selected builds and destinations
                                  before starting this release.
                                </span>
                              </div>
                            ) : (
                              <>
                                <div
                                  className={`campaignGateSummary campaignGateSummary--${preflight.status}`}
                                >
                                  {preflight.status === 'ready' ? (
                                    <ShieldCheck size={19} />
                                  ) : (
                                    <ShieldAlert size={19} />
                                  )}
                                  <div>
                                    <strong>
                                      {preflight.status === 'ready'
                                        ? 'All required checks passed at the last check'
                                        : `${blockers.length} check${blockers.length === 1 ? '' : 's'} need attention`}
                                    </strong>
                                    <span>
                                      {preflight.status === 'ready'
                                        ? 'The selected builds, destinations and required ownership records were present.'
                                        : campaignBlockers.length
                                          ? `${campaignBlockers.length} release-wide check${campaignBlockers.length === 1 ? '' : 's'} need attention.${targetBlockerCount ? ` ${targetBlockerCount} target check${targetBlockerCount === 1 ? ' appears' : 's appear'} beside the affected release units.` : ''}`
                                          : 'See the affected release units for their specific hold-ups.'}
                                    </span>
                                  </div>
                                </div>
                                {campaignBlockers.map((gate) => (
                                  <div
                                    className={`campaignGate campaignGate--${gate.status}`}
                                    key={gate.deliveryCampaignGateId}
                                  >
                                    <span>
                                      <AlertCircle size={14} />
                                    </span>
                                    <div>
                                      <strong>{gate.title}</strong>
                                      <small>
                                        {gate.resolution || gate.detail}
                                      </small>
                                    </div>
                                  </div>
                                ))}
                                <details className="campaignGateDetails">
                                  <summary>
                                    <ChevronDown size={14} /> View all{' '}
                                    {campaign.gates.length} checks
                                  </summary>
                                  <div>
                                    {[...(campaign.gates || [])]
                                      .sort(
                                        (a, b) =>
                                          (a.status === 'ready' ? 1 : 0) -
                                          (b.status === 'ready' ? 1 : 0)
                                      )
                                      .map((gate) => (
                                        <div
                                          className={`campaignGate campaignGate--${gate.status}`}
                                          key={gate.deliveryCampaignGateId}
                                        >
                                          <span>
                                            {gate.status === 'ready' ? (
                                              <Check size={14} />
                                            ) : (
                                              <AlertCircle size={14} />
                                            )}
                                          </span>
                                          <div>
                                            <strong>{gate.title}</strong>
                                            <small>
                                              {gate.resolution || gate.detail}
                                            </small>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </details>
                              </>
                            )}
                          </section>
                        </div>
                      </div>
                    ) : null}
                    <div className="campaignRowFooter">
                      <button
                        type="button"
                        className="campaignReviewButton"
                        onClick={() =>
                          setExpandedCampaignId(
                            expanded ? '' : campaign.deliveryCampaignId
                          )
                        }
                      >
                        <ChevronDown size={15} />
                        {expanded ? 'Hide details' : 'Review release'}
                      </button>
                      <span>{progress}</span>
                      <div className="campaignActions">
                        {campaign.status === 'planned' ? (
                          <button
                            type="button"
                            className="secondaryButton"
                            disabled={
                              busy === `cancel:${campaign.deliveryCampaignId}`
                            }
                            onClick={() => void cancelCampaign(campaign)}
                          >
                            <Trash2 size={15} />
                            Cancel plan
                          </button>
                        ) : null}
                        {campaign.status !== 'completed' &&
                        campaign.status !== 'cancelled' ? (
                          <>
                            <button
                              type="button"
                              className="secondaryButton"
                              disabled={
                                busy ===
                                `preflight:${campaign.deliveryCampaignId}`
                              }
                              onClick={() => runPreflight(campaign)}
                            >
                              {busy ===
                              `preflight:${campaign.deliveryCampaignId}` ? (
                                <RefreshCw className="spin" size={16} />
                              ) : (
                                <ScanSearch size={16} />
                              )}
                              {preflight.total
                                ? 'Check again'
                                : 'Check release'}
                            </button>
                            <button
                              type="button"
                              className="primaryButton"
                              disabled={
                                busy === campaign.deliveryCampaignId ||
                                !readyTargetKeys.length
                              }
                              onClick={() => startCampaign(campaign)}
                            >
                              {busy === campaign.deliveryCampaignId ? (
                                <RefreshCw className="spin" size={16} />
                              ) : (
                                <Rocket size={16} />
                              )}
                              {readyTargetKeys.length < remainingTargetCount
                                ? `Start ${readyTargetKeys.length} ready target${readyTargetKeys.length === 1 ? '' : 's'}`
                                : campaign.status === 'planned'
                                  ? 'Start release'
                                  : 'Move to next step'}
                            </button>
                          </>
                        ) : campaign.status === 'completed' ? (
                          <span className="campaignComplete">
                            <Check size={15} /> Delivery complete
                          </span>
                        ) : (
                          <span className="campaignCancelled">
                            Release cancelled
                          </span>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      ) : null}
      {view === 'campaigns' && workspace.shipments.length ? (
        <details className="legacyShipmentHistory">
          <summary>
            Earlier OTA release history · {workspace.shipments.length}
          </summary>
          <p>
            Kept for audit. Plan new releases here so API, web and app updates
            can move together.
          </p>
        </details>
      ) : null}
    </div>
  );
}

function SectionHeading({
  eyebrow,
  title,
  description,
  count
}: {
  eyebrow: string;
  title: string;
  description: string;
  count: string;
}) {
  return (
    <div className="releaseSectionHeading">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <span>{count}</span>
    </div>
  );
}
function Empty({
  icon,
  title,
  body
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="releaseControlEmpty">
      {icon}
      <div>
        <strong>{title}</strong>
        <span>{body}</span>
      </div>
    </div>
  );
}

function RuntimeObserverSetup({
  projects,
  connections,
  draft,
  setDraft,
  issued,
  busy,
  onIssue,
  onRotate,
  onRevoke
}: {
  projects: { id: string; name: string }[];
  connections: RuntimeObserverConnectionRow[];
  draft: {
    projectId: string;
    name: string;
    environment: string;
    namespace: string;
  };
  setDraft: React.Dispatch<
    React.SetStateAction<{
      projectId: string;
      name: string;
      environment: string;
      namespace: string;
    }>
  >;
  issued: IssuedRuntimeObserverConnectionRow | null;
  busy: string;
  onIssue: () => void;
  onRotate: React.Dispatch<RuntimeObserverConnectionRow>;
  onRevoke: React.Dispatch<RuntimeObserverConnectionRow>;
}) {
  const [prometheusUrl, setPrometheusUrl] = useState(
    'http://prometheus-operated.monitoring.svc.cluster.local:9090'
  );
  const [cindroUrl, setCindroUrl] = useState(window.location.origin);
  const installCommand = issued
    ? `read -rsp "Cindro observer token: " CINDRO_OBSERVER_TOKEN; echo
read -rp "Cindro URL: " CINDRO_URL
read -rp "Prometheus URL: " PROMETHEUS_URL
kubectl -n ${issued.namespace} create secret generic cindro-runtime-observer --from-literal=token="$CINDRO_OBSERVER_TOKEN" --dry-run=client -o yaml | kubectl apply -f -
unset CINDRO_OBSERVER_TOKEN
helm upgrade --install cindro-runtime-observer oci://ghcr.io/nerds-cooking/charts/cindro-runtime-observer --version 0.1.0 --namespace ${issued.namespace} --set-string kubernetesNamespace=${issued.namespace} --set-string cindroUrl="$CINDRO_URL" --set-string prometheusUrl="$PROMETHEUS_URL"
unset CINDRO_URL PROMETHEUS_URL`
    : '';
  return (
    <section className="runtimeObserverSetup">
      <div className="runtimeObserverSetupIntro">
        <div>
          <span className="eyebrow">Outbound-only bridge</span>
          <h3>Connect one namespace to one Cindro project</h3>
          <p>
            The pod reads fixed kube-state-metrics queries from Prometheus and
            sends normalized replica state to Cindro. It has no inbound service,
            no Kubernetes API token, no action permissions and no access to
            Cindro data.
          </p>
        </div>
        <ShieldCheck size={24} />
      </div>
      <div className="runtimeObserverSetupGrid">
        <label>
          <span>Project</span>
          <select
            value={draft.projectId}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                projectId: event.target.value
              }))
            }
          >
            <option value="">Choose project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Environment</span>
          <input
            value={draft.environment}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                environment: event.target.value.toLowerCase()
              }))
            }
            placeholder="production"
          />
        </label>
        <label>
          <span>Kubernetes namespace</span>
          <input
            value={draft.namespace}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                namespace: event.target.value.toLowerCase()
              }))
            }
            placeholder="skillora-production"
          />
        </label>
        <label>
          <span>Connection name</span>
          <input
            value={draft.name}
            onChange={(event) =>
              setDraft((current) => ({ ...current, name: event.target.value }))
            }
            placeholder="Production Prometheus"
          />
        </label>
      </div>
      <div className="runtimeObserverSecurity">
        <ShieldCheck size={17} />
        <div>
          <strong>Credential scope</strong>
          <span>
            Write-only · this project · this environment · this namespace ·
            expires after 180 days · revocable immediately
          </span>
        </div>
        <button
          type="button"
          className="primaryButton"
          disabled={busy === 'runtime-create'}
          onClick={onIssue}
        >
          {busy === 'runtime-create' ? (
            <RefreshCw className="spin" size={15} />
          ) : (
            <KeyRound size={15} />
          )}{' '}
          Issue credential
        </button>
      </div>
      {issued ? (
        <div className="runtimeObserverIssued">
          <header>
            <div>
              <span className="eyebrow">Shown once</span>
              <strong>Install {issued.name}</strong>
            </div>
            <span>
              Expires {new Date(issued.tokenExpiresAt).toLocaleDateString()}
            </span>
          </header>
          <div className="runtimeObserverToken">
            <code>{issued.observerToken}</code>
            <button
              type="button"
              aria-label="Copy observer token"
              onClick={() =>
                navigator.clipboard.writeText(issued.observerToken)
              }
            >
              <Copy size={15} /> Copy
            </button>
          </div>
          <div className="runtimeObserverEndpoints">
            <label>
              <span>Cindro URL</span>
              <input
                value={cindroUrl}
                onChange={(event) => setCindroUrl(event.target.value)}
              />
            </label>
            <label>
              <span>Prometheus service URL</span>
              <input
                value={prometheusUrl}
                onChange={(event) => setPrometheusUrl(event.target.value)}
              />
            </label>
          </div>
          <p>
            Copy the token, then run this in a trusted terminal. Enter the two
            URLs shown above when prompted. The silent token prompt keeps it out
            of shell history; the token is stored only in a Kubernetes Secret.
          </p>
          <pre>
            <code>{installCommand}</code>
          </pre>
          <div className="runtimeObserverIssuedWarning">
            <AlertCircle size={15} />
            Do not put this token in Git, Helm values, a command argument, logs
            or screenshots. Rotate it immediately if it is exposed.
          </div>
        </div>
      ) : null}
      {connections.length ? (
        <div className="runtimeObserverConnections">
          <header>
            <strong>Existing observers</strong>
            <span>
              {connections.filter((item) => item.status === 'active').length}{' '}
              active
            </span>
          </header>
          {connections.map((connection) => (
            <div
              className="runtimeObserverConnection"
              key={connection.runtimeObserverConnectionId}
            >
              <span
                className={`releaseStateBadge releaseStateBadge--${connection.status === 'active' ? (connection.lastSeenAt ? 'deployed' : 'unknown') : 'failed'}`}
              >
                <CircleDot size={12} />
                {connection.status === 'revoked'
                  ? 'Revoked'
                  : connection.lastSeenAt
                    ? 'Reporting'
                    : 'Awaiting data'}
              </span>
              <div>
                <strong>{connection.name}</strong>
                <span>
                  {projects.find(
                    (project) => project.id === connection.projectId
                  )?.name || 'Project'}{' '}
                  · {connection.environment} · {connection.namespace}
                </span>
              </div>
              <small>
                {connection.lastSeenAt
                  ? `Last seen ${formatRelativeDate(connection.lastSeenAt)}`
                  : `Token ${connection.tokenPrefix}…`}
              </small>
              {connection.status === 'active' ? (
                <div>
                  <button
                    type="button"
                    className="secondaryButton"
                    disabled={busy.startsWith('runtime-')}
                    onClick={() => onRotate(connection)}
                  >
                    Rotate
                  </button>
                  <button
                    type="button"
                    className="secondaryButton"
                    disabled={busy.startsWith('runtime-')}
                    onClick={() => onRevoke(connection)}
                  >
                    Revoke
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export { ShipmentsPage };
